"use client";
import React, { Fragment, useEffect } from "react";
import Icon from "../icons/Icons";
import Image from "next/image";
import { AnimatePresence, motion } from "framer-motion";
import { useAppDispatch } from "@/redux/Hooks";
import { openHelpModal } from "@/redux/features/HelpModal/helpModalSlice";
import { useLoginModal } from "@/utils/useLoginModal";
import { useIsMobile } from "@/utils/useIsMobile";
import { MenuItem } from "@/type/commonTypes";
import { lockBodyScroll, unlockBodyScroll } from "@/utils/commonFunctions";
import { BLOG_ROOT, DASHBOARD_ROOT, ROOT_URL } from "@/utils/gloableVariables";
import Link from "next/link";
import { deleteCookie } from "@/utils/authService";

export default function HeaderSidebar({
  isOpen,
  isSafari,
  menuItems,
  activeMenu,
  toggleMenuAction,
  toggleSidebarAction,
}: {
  isOpen: boolean;
  isSafari: boolean;
  menuItems: MenuItem[];
  activeMenu: string | null;
  toggleMenuAction: (label: string) => void;
  toggleSidebarAction: () => void;
}) {
  const dispatch = useAppDispatch();
  const { isLoggedIn, open, setModalKey, setLoggedIn } = useLoginModal();

  const isTabDevice = useIsMobile(991);
  const isMobileDevice = useIsMobile(631);

  useEffect(() => {
    if (isOpen) {
      lockBodyScroll();
      return () => {
        unlockBodyScroll();
      };
    }
  }, [isOpen]);

  const handleLogout = () => {
    deleteCookie("authToken", { domain: ROOT_URL });
    deleteCookie("token", { domain: ROOT_URL });
    setLoggedIn(false);
  };

  const customMenu: MenuItem = {
    label: "My Account",
    href: "",
    iconName: "",
    useWrapper: null,
    submenu: [
      {
        label: "Dashboard",
        href: "",
        icon: "dashboard-menu.svg",
      },
      {
        label: "My Profile",
        href: "view-personal-details",
        icon: "profile-menu.svg",
      },
      {
        label: "My Transactions",
        href: "my-transaction",
        icon: "transaction-menu.svg",
      },
    ],
  };

  const allMenuItems = [customMenu, ...menuItems];

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          key="sidebar"
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
          className="fixed lg:hidden z-5000 inset-0 w-full px-5 bg-white shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between">
            <a
              href="https://www.mysiponline.com/"
              className="sticky w-150 xs:w-45 top-0 z-10 block py-3 bg-white shrink-0"
              aria-label="MySIPOnline Logo"
            >
              <Icon.headerLogoIcon className="fill-(--primary) w-full" />
            </a>

            <button
              onClick={toggleSidebarAction}
              className="py-2 sm:py-3"
              aria-label="Click Here to Open the Side Drawer"
            >
              <Icon.headerDrawerIcon className="w-9 h-9" isOpen={true} />
            </button>
          </div>

          {!isMobileDevice && isTabDevice && (
            <div className="max-h-[calc(100vh-140px)] overflow-y-auto scrollbar-xs grid gap-6 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-4">
              {allMenuItems.map((item) => (
                <div key={item.label} className="p-2.5">
                  <div className="flex items-center justify-start w-full gap-3.5 text-gray-800 transition-colors">
                    {/* <Image src={`${ROOT}Mobile/images/new-icon/${item.iconName}.svg`} alt="icon" width={20} height={20} className="flex-shrink-0 w-5 h-5" /> */}

                    <span className="text-lg text-[#292d32] font-bold">
                      {item.label}
                    </span>
                    {item.label === "Pro Services" && (
                      <Image
                        src={`${ROOT_URL}images/menu_icon/Pro_tag.svg`}
                        alt="Pro Services Icon"
                        width={22}
                        height={22}
                        className="ml-1 w-5.5 h-5.5"
                      />
                    )}
                  </div>

                  <ul className="mt-4.5 space-y-4">
                    {item.submenu.map((subItem) =>
                      subItem.children && subItem.children.length > 0 ? (
                        <Fragment key={subItem.label}>
                          <li>
                            <p className="text-sm font-semibold text-primaryBlue uppercase">
                              {subItem.label}
                            </p>
                          </li>
                          {subItem.children.map((child) => (
                            <li key={child.label}>
                              <a
                                href={`${ROOT_URL}${child.href}`}
                                className="flex items-center justify-items-start gap-3"
                              >
                                <Image
                                  src={`${ROOT_URL}images/menu_icon/${child.icon}`}
                                  alt="icon"
                                  width={25}
                                  height={25}
                                  className="shrink-0"
                                />
                                <p className="text-base font-normal text-[#292d32]">
                                  {child.label}
                                </p>
                              </a>
                            </li>
                          ))}
                        </Fragment>
                      ) : (
                        <li key={subItem.label}>
                          <a
                            href={`${subItem.isBlog ? BLOG_ROOT : item.label === "My Account" ? DASHBOARD_ROOT : ROOT_URL}${subItem.href}`}
                            className="flex items-center justify-items-start gap-3  "
                          >
                            <Image
                              src={
                                item.label === "My Account"
                                  ? `/next_assets/images/common-images/${subItem.icon}`
                                  : `${ROOT_URL}images/menu_icon/${subItem.icon}`
                              }
                              alt="icon"
                              width={25}
                              height={25}
                              className="shrink-0"
                            />
                            <p className="text-base font-normal text-[#292d32]">
                              {subItem.label}
                            </p>
                          </a>
                        </li>
                      ),
                    )}
                  </ul>
                </div>
              ))}
            </div>
          )}

          {isMobileDevice && (
            <div
              className="overflow-y-auto scrollbar-none"
              style={{
                maxHeight: isSafari
                  ? "calc(100vh - 32vh)"
                  : "calc(100vh - 140px)",
              }}
            >
              {allMenuItems.map((item) => (
                <div key={item.label}>
                  <button
                    onClick={() => toggleMenuAction(item.label)}
                    aria-label="Click Here to Active Menu"
                    className="flex items-center justify-items-start w-full p-2.5 gap-3.5 text-gray-800 transition-colors"
                  >
                    {item.label === "My Account" ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        className="shrink-0 w-5 h-5"
                      >
                        <circle
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="#000"
                          strokeLinejoin="round"
                          fill="none"
                        />
                        <circle
                          cx="12"
                          cy="9"
                          r="3"
                          stroke="#000"
                          strokeLinejoin="round"
                          fill="none"
                        />
                        <path
                          stroke="#000"
                          strokeLinejoin="round"
                          d="M18.7966 19.3165C18.0429 16.2637 15.286 14 12.0003 14C8.71471 14 5.9578 16.2637 5.2041 19.3165"
                          fill="none"
                        />
                      </svg>
                    ) : (
                      <Image
                        src={`${ROOT_URL}Mobile/images/new-icon/${item.iconName}.svg`}
                        alt="icon"
                        width={20}
                        height={20}
                        className="shrink-0 w-5 h-5"
                      />
                    )}

                    <span
                      className={`text-base text-[#292d32] ${activeMenu === item.label ? "font-semibold" : "font-normal"}`}
                    >
                      {item.label}
                    </span>

                    {item.label === "Pro Services" && (
                      <Image
                        src={`${ROOT_URL}images/menu_icon/Pro_tag.svg`}
                        alt="Pro Services Icon"
                        width={22}
                        height={22}
                        className="ml-1 w-5.5 h-5.5"
                      />
                    )}

                    <Icon.dropdownDownArrow
                      className={`text-primaryBlack ml-auto transition-all duration-350 ease-in-out ${activeMenu === item.label ? "rotate-180" : ""}`}
                    />
                  </button>

                  <AnimatePresence mode="wait">
                    {activeMenu === item.label && (
                      <motion.div
                        key="submenu"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="overflow-hidden"
                      >
                        <ul className="rounded-lg bg-[#f0f0f0] px-4 py-4.5 mt-2">
                          {item.submenu.map((subItem) =>
                            subItem.children && subItem.children.length > 0 ? (
                              <Fragment key={subItem.label}>
                                <li className="mb-3">
                                  <p className="text-sm font-semibold text-primaryBlue/90 uppercase">
                                    {subItem.label}
                                  </p>
                                </li>
                                {subItem.children.map((child) => (
                                  <li
                                    key={child.label}
                                    className="last-of-type:mb-0 mb-5"
                                  >
                                    <a
                                      href={`${ROOT_URL}${child.href}`}
                                      className="flex items-center justify-items-start gap-3   text-gray-700 hover:text-[#1e3c87]"
                                    >
                                      <Image
                                        src="https://www.mysiponline.com/Mobile/images/new-icon/arrow.svg"
                                        alt="Arrow Icon"
                                        width={20}
                                        height={10}
                                        className="shrink-0"
                                      />

                                      <p className="text-base font-normal text-[#292d32]">
                                        {child.label}
                                      </p>
                                    </a>
                                  </li>
                                ))}
                              </Fragment>
                            ) : (
                              <li
                                key={subItem.label}
                                className="last-of-type:mb-0 mb-5"
                              >
                                <a
                                  href={`${subItem.isBlog ? BLOG_ROOT : item.label === "My Account" ? DASHBOARD_ROOT : ROOT_URL}${subItem.href}`}
                                  className="flex items-center justify-items-start gap-3   text-gray-700 hover:text-[#1e3c87]"
                                >
                                  <Image
                                    src="https://www.mysiponline.com/Mobile/images/new-icon/arrow.svg"
                                    alt="Arrow Icon"
                                    width={20}
                                    height={10}
                                    className="shrink-0"
                                  />

                                  <p className="text-base font-normal text-[#292d32]">
                                    {subItem.label}
                                  </p>
                                </a>
                              </li>
                            ),
                          )}
                        </ul>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center justify-center gap-3.5 bg-white border-t border-[#e3e3e3] absolute bottom-0 left-0 right-0 px-3 xs:px-6 py-4.5">
            <button
              onClick={() => dispatch(openHelpModal())}
              className="block w-full relative rounded-xl px-1.5 py-2.5 xxs:p-2.5 font-normal text-center text-sm xxs:text-base border transition-all duration-400 ease-in-out bg-white text-(--text) border-(--secondary) hover:bg-[#d8e7ff]"
            >
              Need Help?
            </button>
            {isLoggedIn ? (
              <a
                href={`${ROOT_URL}logout.php`}
                onClick={handleLogout}
                className="block px-1.5 py-2.5 xxs:p-2.5 w-full text-center rounded-xl border border-[#ec4b4b] font-normal text-sm xxs:text-base transition-all duration-300 ease-in-out text-white bg-[#ec4b4b]"
              >
                Logout
              </a>
            ) : (
              <Link
                href="/login"
                className="block px-1.5 py-2.5 xxs:p-2.5 w-full text-center rounded-xl border border-(--secondary) font-normal text-sm xxs:text-base transition-all duration-300 ease-in-out text-white bg-(--secondary)"
              >
                Open Free Account
              </Link>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
